import { useState, useEffect } from "react";
import { AppRoute } from "@/App";
import { PrognosisDomain } from "@/types/Prognosis";
import { useNavigate } from "react-router-dom";
import { usePrognosisRepository } from "@/services/prognosisRepository";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { CONFIG } from "@/config";
import { userInfoRepository } from "@/services/userInfoRepository";
import { ZodiacDomain } from "@/types/Zodiac";

export interface ZodiacPrognosisScreenViewModel {
  prognoses: PrognosisDomain[];
  isSubscribed: boolean | null;
  selectedPrognose: PrognosisDomain | null;
  isDescriptionOpen: boolean;
  setIsDescriptionOpen: (isOpen: boolean) => void;
  isLoading: boolean;
  error: string | null;
  isPrognosisForYear: boolean;
  selectPrognosisForYear: () => void;
  selectPrognosis: (prognosis: PrognosisDomain) => void;
  navigateToHome: () => void;
  navigateToAstroPrognosis: () => void;
  navigateToTelegram: () => void;
  navigateToSubscribe: () => void;
  zodiac: ZodiacDomain | null;
}

export const useZodiacPrognosisScreenViewModel = (): ZodiacPrognosisScreenViewModel => {
  const [prognoses, setPrognoses] = useState<PrognosisDomain[]>([]);
  const [selectedPrognose, setSelectedPrognose] =
    useState<PrognosisDomain | null>(null);
  const [isPrognosisForYear, setIsPrognosisForYear] = useState(false);

  const [zodiac, setZodiac] = useState<ZodiacDomain | null>(null);

  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [isSubscribed, setIsSubscribed] = useState<boolean | null>(null);
  const { subscription, isLoading: subscriptionLoading } = useSubscription();

  const prognosisRepository = usePrognosisRepository();

  const loadPrognoses = async () => {
    setError(null);
    if (subscriptionLoading) {
      return;
    }
    try {
      setIsLoading(true);
      const isSubscribed = updateSubscription();
      await updateUserZodiac();
      if (isSubscribed) {
        await initZodiacPrognosis();
      }
    } catch (err) {
      setError("Ошибка при загрузке прогнозов");
    } finally {
      setIsLoading(false);
    }
  };

  const updateSubscription = (): boolean => {
    const isSubscribedValue = subscription?.isSubscribed ?? false;
    setIsSubscribed(isSubscribedValue);
    return isSubscribedValue;
  };

  const initZodiacPrognosis = async () => {
    try {
      setIsLoading(true);
      const response = await prognosisRepository.getZodiacPrognosis();
      setPrognoses(response);
      setSelectedPrognose(response[0] || null);
    } catch (error) {
      setError("Ошибка при загрузке прогнозов");
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserZodiac = async () => {

    try {
      const homeScreen = await userInfoRepository.getHomeInfo();
      const zodiac = homeScreen.astrologicalSign;
      setZodiac(zodiac);
    } catch (error) {
      setError("Ошибка при загрузке прогнозов");
    }

  }

  const selectPrognosis = (prognosis: PrognosisDomain) => {
    setSelectedPrognose(prognosis);
    setIsPrognosisForYear(false);
  };

  const selectPrognosisForYear = async () => {
    setIsPrognosisForYear(true);
    const yearPrognosis = await prognosisRepository.getYearPrognosis();
    setSelectedPrognose(yearPrognosis);
  };

  const navigateToHome = () => {
    navigate(AppRoute.Home);
  };

  const navigateToAstroPrognosis = () => {
    navigate(AppRoute.AstroPrognosis);
  };

  const navigateToTelegram = () => {
    window.open(CONFIG.links.telegramAstrolog, "_blank");
  };

  const navigateToSubscribe = () => {
    navigate(AppRoute.Subscription);
  };

  useEffect(() => {
    loadPrognoses();
  }, [subscription, subscriptionLoading]);

  return {
    prognoses,
    isPrognosisForYear,
    isSubscribed,
    selectPrognosisForYear,
    selectedPrognose,
    selectPrognosis,
    isLoading,
    error,
    isDescriptionOpen,
    setIsDescriptionOpen,
    navigateToHome,
    navigateToAstroPrognosis,
    navigateToTelegram,
    navigateToSubscribe,
    zodiac,
  };
};
